import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../utils";

export function usePortaledDropdown(isOpen, triggerRef, { width = 176, onClose } = {}) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      top: rect.bottom + 4,
      left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
    });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, width]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onScrollOrResize = () => updatePosition();
    const onDocMouseDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      onClose?.();
    };

    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    document.addEventListener("mousedown", onDocMouseDown);

    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, width, onClose]);

  return { menuRef, pos };
}

export function PortaledDropdown({
  isOpen,
  triggerRef,
  onClose,
  width = 176,
  className,
  children,
  role = "menu",
}) {
  const { menuRef, pos } = usePortaledDropdown(isOpen, triggerRef, { width, onClose });

  if (!isOpen) return null;

  const menu = (
    <div
      ref={menuRef}
      className={cn(
        "fixed z-[500] bg-white rounded-lg shadow-lg border border-gray-100 py-1",
        className
      )}
      style={{ top: pos.top, left: pos.left, width }}
      onClick={(e) => e.stopPropagation()}
      role={role}
    >
      {children}
    </div>
  );

  return typeof document !== "undefined" ? createPortal(menu, document.body) : menu;
}
